<script setup lang="ts">
import type { Property } from '~/core/entities/property';

import { cn } from '@/utils';
import { usePropertyHeroShare } from '~/composables/usePropertyHeroShare';
import { useToggleFavoriteSync } from '~/composables/useToggleFavoriteSync';

import { HoverExpandButton } from '~/components/HoverExpandButton';
import { ShareContent } from '~/components/ShareContent';

import IconMaterialSymbolsShare from '~icons/material-symbols-light/share-outline';
import IconMaterialSymbolsSharpStar from '~icons/material-symbols-light/star';
import IconMaterialSymbolsSharpStarOutline from '~icons/material-symbols-light/star-outline';

interface PropertyHeroShareControlsProps {
	property: Property | null;
}

const props = defineProps<PropertyHeroShareControlsProps>();

const propertyRef = computed(() => props.property);

const {
	showShareContent,
	shareContentRef,
	shareContentButton,
	shareUrl,
	toggleShareContent,
	shareMobile,
	handleShareClicked
} = usePropertyHeroShare({ property: propertyRef });

const route = useRoute();

const eventData = {
	sectionName: 'property hero share controls',
	currentPage: route.fullPath,
	tagExclusive: props.property?.isExclusive,
	companyName: props.property?.company?.name,
	agentName: props.property?.agent?.name,
	clickProperty: true
};

// Use reactive computed values for the composable
const propertyId = computed(() => props.property?.id);
const commercialId = computed(() => props.property?.commercialId);
const initialFavoriteState = computed(
	() => props.property?.inUserWishlist ?? false
);

// useToggleFavoriteSync is synchronous - no await needed for SSR performance
const { isFavorite, isValidData, handleToggleFavorite } = useToggleFavoriteSync(
	propertyId,
	commercialId,
	eventData,
	'hero section',
	initialFavoriteState
);

const favoriteButtonAriaLabel = computed<string>(() =>
	isFavorite.value ? 'Remover da minha coleção' : 'Adicionar à minha coleção'
);
</script>

<template>
	<div
		class="absolute right-4 top-[4.5rem] z-20 md:right-5 md:top-[5.5rem] lg:right-12"
	>
		<HoverExpandButton
			class="flex size-10 justify-center lg:hidden"
			@click="shareMobile"
		>
			<template #icon>
				<IconPilarSend48Degrees class="size-5" />
			</template>
		</HoverExpandButton>

		<div class="relative hidden flex-row gap-4 lg:flex">
			<!-- Favorite button wrapped in ClientOnly to prevent hydration mismatch -->
			<!-- The wishlist state differs between server (empty) and client (populated) -->
			<ClientOnly v-if="isValidData">
				<template #fallback>
					<HoverExpandButton
						class="flex size-12 justify-center"
						aria-label="Adicionar à minha coleção"
						:aria-pressed="false"
					>
						<template #icon>
							<IconMaterialSymbolsSharpStarOutline
								class="size-6 scale-110 text-white group-hover:[@media(hover:hover)]:!text-beige"
							/>
						</template>
					</HoverExpandButton>
				</template>
				<HoverExpandButton
					ref="favoriteHeroButton"
					:class="
						cn(
							'flex size-12 justify-center',
							isFavorite &&
								'!bg-beige !text-primary hover:[@media(hover:hover)]:!bg-beige-light-2'
						)
					"
					:aria-label="favoriteButtonAriaLabel"
					:aria-pressed="isFavorite"
					@click="handleToggleFavorite"
				>
					<template #icon>
						<component
							:is="
								isFavorite
									? IconMaterialSymbolsSharpStar
									: IconMaterialSymbolsSharpStarOutline
							"
							:class="
								cn(
									'size-6 scale-110',
									!isFavorite &&
										'group-hover:[@media(hover:hover)]:!text-beige',
									isFavorite ? 'text-primary' : 'text-white'
								)
							"
						/>
					</template>
				</HoverExpandButton>
			</ClientOnly>

			<HoverExpandButton
				ref="shareContentButton"
				class="flex size-12 justify-center"
				@click="toggleShareContent"
			>
				<template #icon>
					<IconPilarSend48Degrees class="size-5" />
				</template>
			</HoverExpandButton>

			<div
				v-show="showShareContent"
				ref="shareContentRef"
				class="absolute right-0 top-full z-40 mt-2 w-[180px] border border-white-dark-3 bg-white p-[0.7rem_1rem]"
			>
				<ShareContent
					v-model="showShareContent"
					variant="dropdown"
					mail-subject="Leia mais sobre este imóvel"
					:content-to-share="shareUrl"
					show-labels
					@share-clicked="handleShareClicked"
				/>
			</div>
		</div>
	</div>
</template>
